import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Typography, Grid2, Collapse, Button } from '@mui/material';
import { StaticDatePicker } from '@mui/x-date-pickers';
import { dayjs, Dayjs } from '../../utils/dayjs-config';
import { Appointment } from '../../types';
import { MAX_DATE_RANGE } from '../../constants';
import { formatDateToMMDDYYYY, findFreeSlots, filterSlotsByMinimumNoticeThreshold } from '../../time/timeUtils';

interface DatePickerProps {
    appointment: Appointment;
    handleAppointment: (appointment: Appointment) => void;
    updateAppointment: boolean;
    initialDate: Dayjs;
    appointmentDuration: number;
    scheduleCache: Map<string, string>;
    availableSlotsByDate?: Map<string, string[]>;
    disableDates: boolean;
    setAvailableSlots: (availableSlots: string[]) => void;
    goToNextStep: () => void;
    minimumBookingNoticeHours: number;
    allowPastDates?: boolean;
}

const DatePicker: React.FC<DatePickerProps> = ({
    appointment,
    handleAppointment,
    updateAppointment,
    initialDate,
    appointmentDuration,
    scheduleCache,
    availableSlotsByDate,
    disableDates,
    setAvailableSlots,
    goToNextStep,
    minimumBookingNoticeHours,
    allowPastDates = false,
}) => {
    const [disabledDatesList, setDisabledDatesList] = useState<Dayjs[]>([]);
    const [showEmptySlotsMessage, setShowEmptySlotsMessage] = useState(false);
    const normalizedMinimumBookingNoticeHours = allowPastDates ? 0 : Math.max(0, minimumBookingNoticeHours ?? 1);
    const getEarliestBookingDateTime = useCallback(() =>
        normalizedMinimumBookingNoticeHours > 0 ? dayjs.tz().add(normalizedMinimumBookingNoticeHours, 'hour') : null,
    [normalizedMinimumBookingNoticeHours]);

    const getFilteredSlotsForDate = useCallback((date: Dayjs, scheduleTimeBitString?: string): string[] => {
        const formattedDate = date.format('MMDDYYYY');
        const isToday = dayjs.tz().isSame(date, 'day');
        const disablePastTimes = !allowPastDates && isToday;
        const earliestBookingDateTime = getEarliestBookingDateTime();

        if (availableSlotsByDate) {
            const explicitSlots = availableSlotsByDate.get(formattedDate) ?? [];
            const currentTimeInMinutes = dayjs.tz().hour() * 60 + dayjs.tz().minute();
            const slotsWithoutPastTimes = disablePastTimes
                ? explicitSlots.filter((slot) => {
                    const hours = parseInt(slot.slice(0, 2), 10);
                    const minutes = parseInt(slot.slice(2, 4), 10);
                    return (hours * 60 + minutes) >= currentTimeInMinutes;
                })
                : explicitSlots;
            return allowPastDates
                ? slotsWithoutPastTimes
                : filterSlotsByMinimumNoticeThreshold(slotsWithoutPastTimes, date, earliestBookingDateTime);
        }

        if (!scheduleTimeBitString) {
            return [];
        }

        const freeSlots = findFreeSlots(scheduleTimeBitString, appointmentDuration, disablePastTimes);
        return allowPastDates
            ? freeSlots
            : filterSlotsByMinimumNoticeThreshold(freeSlots, date, earliestBookingDateTime);
    }, [allowPastDates, appointmentDuration, availableSlotsByDate, getEarliestBookingDateTime]);

    // The first day after `after` (or from today) that still has open times.
    const findNextAvailableDate = useCallback((after?: Dayjs | null): Dayjs | null => {
        const today = dayjs.tz();
        for (let i = 0; i <= MAX_DATE_RANGE; i++) {
            const date = today.add(i, 'day');
            if (after && !date.isAfter(after, 'day')) continue;
            const slots = getFilteredSlotsForDate(date, scheduleCache.get(date.format('MMDDYYYY')));
            if (slots.length > 0) return date.startOf('day');
        }
        return null;
    }, [getFilteredSlotsForDate, scheduleCache]);

    // Open on the next day with times when nothing is picked yet and today has
    // none, so the calendar never shows "no times" and then jumps.
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(() => {
        if (allowPastDates || !disableDates || appointment.appointmentDate) return initialDate;
        const today = dayjs.tz();
        if (getFilteredSlotsForDate(today, scheduleCache.get(today.format('MMDDYYYY'))).length > 0) return initialDate;
        return findNextAvailableDate(today) ?? initialDate;
    });

    const formatDate = (date: Dayjs | null): string => {
        if (!date) return '';
        return formatDateToMMDDYYYY(date);
    };

    //For the selected date, set the available time slots
    const handleAvailableSlots = useCallback((date: Dayjs | null): boolean => {
        let hasFreeSlots = false;

        if (date) {
            const formattedDate: string = date.format('MMDDYYYY');
            const scheduleTimeBitString = scheduleCache.get(formattedDate);

            if (scheduleTimeBitString || availableSlotsByDate) {
                const filteredSlots = getFilteredSlotsForDate(date, scheduleTimeBitString);
                setAvailableSlots(filteredSlots);
                if (filteredSlots.length === 0) {
                    setShowEmptySlotsMessage(true);
                }
                else {
                    hasFreeSlots = true;
                    setShowEmptySlotsMessage(false);
                }

                if (formattedDate !== appointment.appointmentDate) {
                    //only update appointment if it's a new date
                    if (updateAppointment) {
                        const appointmentDate = date ? formatDate(date) : '';
                        appointment.appointmentDate = appointmentDate;
                        appointment.appointmentTime = '';
                        handleAppointment({ ...appointment });
                    }
                }
            } else {
                setAvailableSlots([]);
                setShowEmptySlotsMessage(true);
            }
        }

        return hasFreeSlots;
    }, [appointment, availableSlotsByDate, getFilteredSlotsForDate, handleAppointment, scheduleCache, setAvailableSlots, updateAppointment]);

    // Re-check slots whenever the notice window changes (runs once on mount, too).
    useEffect(() => {
        handleAvailableSlots(selectedDate);
    }, [handleAvailableSlots, selectedDate]);

    const handleDateChange = (newDate: Dayjs | null) => {
        if (!newDate) return;

        setSelectedDate(newDate);

        //navigate to next screen if date that user selected has free slots
        const hasFreeSlots = handleAvailableSlots(newDate);
        if (hasFreeSlots) {
            goToNextStep();
        }
    };

    // The same, for availability that arrives after the calendar opened.
    // (Read once: selecting a date writes it onto the appointment.)
    const hadDateWhenOpened = useRef(!!appointment.appointmentDate);
    const startedOnAvailableDate = useRef(false);
    useEffect(() => {
        if (startedOnAvailableDate.current || allowPastDates || !disableDates || hadDateWhenOpened.current) return;
        const today = dayjs.tz();
        if (getFilteredSlotsForDate(today, scheduleCache.get(today.format('MMDDYYYY'))).length > 0) {
            startedOnAvailableDate.current = true;
            return;
        }
        const next = findNextAvailableDate(today);
        if (next) {
            startedOnAvailableDate.current = true;
            setSelectedDate(next);
        }
    }, [allowPastDates, disableDates, findNextAvailableDate, getFilteredSlotsForDate, scheduleCache]);

    const nextAvailableDate = useMemo(
        () => (showEmptySlotsMessage ? findNextAvailableDate(selectedDate) : null),
        [findNextAvailableDate, selectedDate, showEmptySlotsMessage]
    );

    // Set list of disabled dates, based on which dates have free time slots for the selected service
    const handleDisableDates = useCallback((scheduleMap: Map<string, string>, serviceDuration: number): void => {
        const disabledDates: Dayjs[] = [];
        const today = dayjs.tz();
        const earliestBookingDateTime = getEarliestBookingDateTime();

        for (let i = 0; i <= MAX_DATE_RANGE; i++) {
            const currentDate = today.add(i, 'day');
            const formattedDate = formatDateToMMDDYYYY(currentDate);
            const scheduleTimeBitString = scheduleMap.get(formattedDate);

            if (scheduleTimeBitString || availableSlotsByDate) {
                const filteredSlots = availableSlotsByDate
                    ? getFilteredSlotsForDate(currentDate)
                    : allowPastDates
                        ? findFreeSlots(scheduleTimeBitString!, serviceDuration, false)
                        : filterSlotsByMinimumNoticeThreshold(
                            findFreeSlots(
                                scheduleTimeBitString!,
                                serviceDuration,
                                currentDate.isSame(today, 'day')
                            ),
                            currentDate,
                            earliestBookingDateTime
                        );
                if (filteredSlots.length === 0) {
                    disabledDates.push(currentDate);
                }
            }
            else {
                disabledDates.push(currentDate);
            }
        }
        setDisabledDatesList(disabledDates);

    }, [allowPastDates, availableSlotsByDate, getEarliestBookingDateTime, getFilteredSlotsForDate]);

    useEffect(() => {
        if (scheduleCache && disableDates) {
            handleDisableDates(scheduleCache, appointmentDuration);
        }
    }, [appointmentDuration, disableDates, handleDisableDates, scheduleCache]);

    return (
        <Grid2 container spacing={2} direction="column" alignItems="center">
            <Grid2>
                <Collapse in={showEmptySlotsMessage}>
                    <Typography color="error" textAlign="center">
                        Sorry, no available times for this date.
                    </Typography>
                    {nextAvailableDate && (
                        <Grid2 container justifyContent="center" sx={{ mt: 1 }}>
                            <Button variant="contained" onClick={() => handleDateChange(nextAvailableDate)}>
                                Next available: {nextAvailableDate.format('ddd, MMM D')}
                            </Button>
                        </Grid2>
                    )}
                </Collapse>
                <StaticDatePicker
                    disablePast={!allowPastDates}
                    value={selectedDate}
                    onChange={handleDateChange}
                    shouldDisableDate={(date) => {
                        if (!disableDates) return false;

                        const today = dayjs.tz();
                        const maxDate = today.add(MAX_DATE_RANGE, 'day');
                        const isDisabledInList = disabledDatesList.some(disabledDate => date.isSame(disabledDate, 'day'));
                        const isBeyondMaxDate = date.isAfter(maxDate, 'day');

                        return isDisabledInList || isBeyondMaxDate;
                    }}
                    slotProps={{ actionBar: { actions: [] } }}
                    sx={{
                        '.MuiPickersToolbar-root': {
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textAlign: 'center',
                            paddingTop: 0,
                            paddingBottom: 0,
                        },
                        '.MuiTypography-h4': {
                            fontSize: '1.5rem',
                            width: '100%',
                            textAlign: 'center',
                        }
                    }}
                />
            </Grid2>
        </Grid2>
    );
};

export default DatePicker;
